import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { Button, Flex } from '@mantine/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	BANK_ACCOUNT_FORM_FIELDS,
	BankAccountCountryOption,
	BankAccountDetails,
	BankAccountFormValues,
} from './types.ts';
import { useBankAccountForm } from './useBankAccountForm.ts';
import type { BankAccountVerifyFn } from './useBankAccountForm.types.ts';
import { BankAccountFormContext } from './BankAccountFormContext.tsx';
import { EntryPathFields } from './EntryPathFields.tsx';
import { BankCountryPicker } from './BankCountryField/BankCountryPicker.tsx';
import { BankCountrySummary } from './BankCountryField/BankCountrySummary.tsx';
import { ruleUsesClassicIbanForm } from './BankAccountForm.utils.ts';
import { COUNTRIES_NAME_CODE_OPTIONS } from '@dealstack/shared/lib/countries/countriesData';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';
import {
	IBAN_COMPONENT_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	hasEditPermission: boolean;
	isRequestLoading: boolean;
	bankAccount: BankAccountDetails | null;
	onSubmit: (formValues: BankAccountFormValues, bankAccountId?: string) => void;
	onCancel?: () => void;
	inferredCountryCode?: string;
	countryOptions?: BankAccountCountryOption[];
	verifyIdentifiers?: BankAccountVerifyFn;
}

export const BankAccountForm = ({
	bankAccount,
	hasEditPermission,
	onSubmit,
	onCancel,
	isRequestLoading,
	inferredCountryCode,
	countryOptions = COUNTRIES_NAME_CODE_OPTIONS,
	verifyIdentifiers,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		bankAccountForm,
		selectedCountryCode,
		storedSummaryValidated,
		onCountryChange,
		formatRules,
		formatRulesLoading,
		formatRulesError,
		activeRule,
		selectedFormatRuleId,
		onFormatChange,
	} = useBankAccountForm({ inferredCountryCode, bankAccount });

	// Country selection is its own step: while it is active the rest of the form hides.
	const [isSelectingCountry, setIsSelectingCountry] = useState(
		() => !inferredCountryCode && !bankAccount
	);
	const [pendingCountryCode, setPendingCountryCode] = useState<string | null>(null);
	// Distinguishes an explicit "Change" from the initial no-country state, so a
	// late-arriving inference or edit reset can close the step, but never a user's.
	const userRequestedCountryChange = useRef(false);

	useEffect(() => {
		if (isSelectingCountry && selectedCountryCode && !userRequestedCountryChange.current) {
			setIsSelectingCountry(false);
		}
	}, [selectedCountryCode, isSelectingCountry]);

	const openCountrySelection = () => {
		userRequestedCountryChange.current = true;
		setPendingCountryCode(selectedCountryCode ?? null);
		setIsSelectingCountry(true);
	};

	const confirmCountrySelection = () => {
		if (!pendingCountryCode) {
			return;
		}
		onCountryChange(pendingCountryCode);
		userRequestedCountryChange.current = false;
		setIsSelectingCountry(false);
	};

	const cancelCountrySelection = () => {
		userRequestedCountryChange.current = false;
		setPendingCountryCode(null);
		if (selectedCountryCode) {
			setIsSelectingCountry(false);
			return;
		}
		onCancel?.();
	};

	// The inference source (person address) can arrive after mount; apply it only while
	// no country has been chosen so it never overrides a user's selection.
	useEffect(() => {
		if (
			!bankAccount &&
			inferredCountryCode &&
			!selectedCountryCode &&
			!userRequestedCountryChange.current
		) {
			bankAccountForm.setValue(BANK_ACCOUNT_FORM_FIELDS.country_code, inferredCountryCode);
		}
	}, [inferredCountryCode]);

	// The lookup only prefills — every field stays editable for anyone allowed to edit.
	const disableBankAccountFields = !hasEditPermission;

	// Edit mode always shows the fieldset — legacy accounts may have details but no country.
	// While a country's rules load, neither fieldset shows (avoids an IBAN → dynamic flash).
	const showChildFieldset =
		(!!selectedCountryCode || !!bankAccount) && !formatRulesLoading && !formatRulesError;
	const isIbanFallback = !activeRule;

	const selectedCountryOption = countryOptions.find(
		(option) => option.value === selectedCountryCode
	);

	// A non-classic rule carrying a `bic` field is the manual-entry tab (e.g. GB
	// SWIFT): the IBAN fieldset's failed-lookup fallback switches to it, so the IBAN
	// tab itself never grows extra fields.
	const manualFormatRule =
		formatRules.find(
			(rule) =>
				!ruleUsesClassicIbanForm(rule) && rule.fields.some((field) => field.key === 'bic')
		) ?? null;

	const formContextValue = useMemo(
		() => ({
			hasEditPermission,
			fieldsDisabled: disableBankAccountFields,
			bankAccount,
			storedSummaryValidated,
			onManualEntrySwitch: manualFormatRule
				? () => onFormatChange(manualFormatRule.id)
				: null,
		}),
		[
			hasEditPermission,
			disableBankAccountFields,
			bankAccount,
			storedSummaryValidated,
			manualFormatRule,
			onFormatChange,
		]
	);

	if (isSelectingCountry) {
		return (
			<Flex direction="column" justify="space-between" gap="1rem" h="100%">
				<BankCountryPicker
					options={countryOptions}
					value={pendingCountryCode}
					onHighlight={setPendingCountryCode}
				/>

				<Flex direction="column" gap="0.5rem">
					<Button
						onClick={confirmCountrySelection}
						disabled={!hasEditPermission || !pendingCountryCode}
						data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_COUNTRY_CONTINUE_BUTTON}
					>
						{tCapManage('bank_account.drawer.country_continue_button')}
					</Button>
					{(selectedCountryCode || onCancel) && (
						<Button
							variant="outline"
							onClick={cancelCountrySelection}
							data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_COUNTRY_BACK_BUTTON}
						>
							{tCapManage('bank_account.drawer.country_back_button')}
						</Button>
					)}
				</Flex>
			</Flex>
		);
	}

	return (
		<FormProvider {...bankAccountForm}>
			<BankAccountFormContext.Provider value={formContextValue}>
				<Flex direction="column" justify="space-between" gap="1rem" h="100%">
					{/* Every identifier, its lookup summary and the holder render in here. */}
					<Flex
						direction="column"
						gap="1rem"
						h="100%"
						className={REPLAY_MASK_CLASS}
						data-sentry-mask
					>
						<BankCountrySummary
							option={selectedCountryOption}
							showDetectedCaption={
								!!selectedCountryCode && selectedCountryCode === inferredCountryCode
							}
							disabled={disableBankAccountFields}
							onChange={openCountrySelection}
						/>

						{formatRulesError && (
							<InfoBox
								direction="row"
								iconVariant="warning"
								rippleColor="red"
								matchBorderToRippleColor
								title={tCapManage('bank_account.drawer.formats_error.title')}
								description={tCapManage('bank_account.drawer.formats_error.text')}
								dataTestId={IBAN_COMPONENT_ELEMENT_ID.BANK_FORMATS_ERROR}
							/>
						)}

						{formatRules.length > 0 && (
							<MantineTabs
								value={selectedFormatRuleId ?? undefined}
								onTabChange={onFormatChange}
								keepMounted={false}
								tabs={formatRules.map((rule) => ({
									label: rule.formatName,
									value: rule.id,
									testId: makeComplexDataTestId(
										IBAN_COMPONENT_ELEMENT_ID.BANK_FORMAT_INPUT,
										rule.formatName
									),
									disabled: disableBankAccountFields,
								}))}
								panels={formatRules.map((rule) => ({
									value: rule.id,
									content: (
										<Flex direction="column" gap="1rem" p="1rem">
											<EntryPathFields
												rule={rule}
												verifyIdentifiers={verifyIdentifiers}
											/>
										</Flex>
									),
								}))}
							/>
						)}

						{showChildFieldset && isIbanFallback && (
							<EntryPathFields rule={null} verifyIdentifiers={verifyIdentifiers} />
						)}
					</Flex>

					<Flex direction="column" gap="0.5rem">
						<Button
							onClick={bankAccountForm.handleSubmit((formValues) =>
								onSubmit(formValues, bankAccount?.id)
							)}
							disabled={
								!hasEditPermission ||
								isRequestLoading ||
								!selectedCountryCode ||
								formatRulesLoading ||
								formatRulesError
							}
							data-testid={IBAN_COMPONENT_ELEMENT_ID.SUBMIT_BUTTON}
						>
							{bankAccount
								? tCapManage('bank_account.drawer.edit_button')
								: tCapManage('bank_account.drawer.add_button')}
						</Button>
						{onCancel && (
							<Button
								variant="outline"
								onClick={onCancel}
								data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_CANCEL_BUTTON}
							>
								{tCapManage('bank_account.drawer.cancel_button')}
							</Button>
						)}
					</Flex>
				</Flex>
			</BankAccountFormContext.Provider>
		</FormProvider>
	);
};
